import { useEffect, useMemo, useRef, useState } from "react";
import PdfExportButton from "../components/PdfExportButton";
import { TrendingUp, BarChart3, RefreshCw, LineChart, PackageX, X, Download, Search, Copy, MapPin, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import InventorySearchSection from "../components/InventorySearchSection";
import SarimaPanel from "../components/SarimaPanel";
import MultiSelect from "../components/MultiSelect";
import { SkeletonBlock, SkeletonRibbon, RefetchPill } from "../components/Loader";
import useMinVisible from "../hooks/useMinVisible";
import useNormalizedFilter from "../hooks/useNormalizedFilter";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const fmtMoney = (n) => "DOP " + new Intl.NumberFormat("es-DO", { maximumFractionDigits: 0 }).format(Math.round(n || 0));
const fmtMoneyShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1e9) return "DOP " + (n / 1e9).toFixed(2) + "B";
  if (v >= 1e6) return "DOP " + (n / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return "DOP " + (n / 1e3).toFixed(0) + "K";
  return fmtMoney(n);
};
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

const DEFAULT_FILTERS = {
  year: [],
  month: [],
  brand: [],
  model: [],
  branch: [],
};

// Stable brand color palette (cycled by brand index).
const BRAND_COLORS = [
  "#f4c400", "#3b82f6", "#16a34a", "#dc2626", "#8b5cf6",
  "#f97316", "#0891b2", "#db2777", "#65a30d", "#7c3aed",
];
const brandColor = (i) => BRAND_COLORS[i % BRAND_COLORS.length];


function KpiCard({ label, value, sub, color }) {
  return (
    <div className="surface p-5" style={{ borderTop: `3px solid ${color}` }}>
      <div className="text-xs font-extrabold text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="font-display font-extrabold mt-2" style={{ fontSize: 28, lineHeight: 1, color: "#111827", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {sub && <div className="text-xs font-semibold text-gray-500 mt-2">{sub}</div>}
    </div>
  );
}

function ForecastLineChart({ points }) {
  if (!points || points.length === 0) {
    return <div className="text-sm text-gray-400 font-semibold py-8 text-center">Sin datos para graficar</div>;
  }
  // Wide landscape viewBox — matches the container's actual aspect ratio at
  // desktop resolutions so the timeline stretches edge-to-edge instead of
  // letterboxing with blank margins.
  const W = 1800;
  const H = 320;
  const padL = 44, padR = 24, padT = 20, padB = 50;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const units = points.map((p) => p.units || 0);
  const max = Math.max(1, ...units);
  const min = 0;
  const stepX = innerW / Math.max(1, points.length - 1);

  const x = (i) => padL + i * stepX;
  const y = (v) => padT + innerH - ((v - min) / (max - min || 1)) * innerH;

  // Split historic vs forecast for two-color polylines
  const histPts = points.map((p, i) => ({ ...p, i })).filter((p) => p.kind === "hist");
  const fcPts = points.map((p, i) => ({ ...p, i })).filter((p) => p.kind === "fc");
  // Connect last hist with first fc for visual continuity
  const fcLinePts = histPts.length && fcPts.length ? [histPts[histPts.length - 1], ...fcPts] : fcPts;

  const lineStr = (arr) => arr.map((p, k) => `${k === 0 ? "M" : "L"} ${x(p.i)} ${y(p.units || 0)}`).join(" ");

  // Linear regression trend across historic points only
  let trendStart = null, trendEnd = null;
  if (histPts.length >= 2) {
    const n = histPts.length;
    const xs = histPts.map((_, i) => i);
    const ys = histPts.map((p) => p.units || 0);
    const mx = xs.reduce((a, b) => a + b, 0) / n;
    const my = ys.reduce((a, b) => a + b, 0) / n;
    const num = xs.reduce((acc, xi, i) => acc + (xi - mx) * (ys[i] - my), 0);
    const den = xs.reduce((acc, xi) => acc + (xi - mx) ** 2, 0) || 1;
    const slope = num / den;
    const intercept = my - slope * mx;
    // Extend trend across full chart including forecast x positions
    const startVal = intercept;
    const endVal = intercept + slope * (points.length - 1);
    trendStart = { i: 0, v: Math.max(0, startVal) };
    trendEnd = { i: points.length - 1, v: Math.max(0, endVal) };
  }

  // Y-axis ticks (4 levels)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(max * t));

  return (
    <div className="w-full overflow-x-auto" data-testid="forecast-chart">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 600, height: 320 }}>
        {/* Grid */}
        {ticks.map((tv) => (
          <g key={tv}>
            <line x1={padL} x2={W - padR} y1={y(tv)} y2={y(tv)} stroke="#f1f5f9" strokeWidth="1"/>
            <text x={padL - 6} y={y(tv) + 3} fontSize="10" fill="#94a3b8" textAnchor="end" fontWeight="700">{tv}</text>
          </g>
        ))}

        {/* Forecast band background */}
        {fcPts.length > 0 && (
          <rect x={x(fcPts[0].i) - stepX / 2} y={padT}
                width={(fcPts.length) * stepX} height={innerH}
                fill="#fef3c7" opacity="0.45"/>
        )}

        {/* Trend line */}
        {trendStart && trendEnd && (
          <line x1={x(trendStart.i)} y1={y(trendStart.v)}
                x2={x(trendEnd.i)} y2={y(trendEnd.v)}
                stroke="#64748b" strokeWidth="1.8" strokeDasharray="6 4" opacity="0.7"/>
        )}

        {/* Historic line (solid yellow) */}
        {histPts.length > 1 && (
          <path d={lineStr(histPts)} fill="none" stroke="#f4c400" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round"/>
        )}
        {/* Forecast line (dashed amber) */}
        {fcLinePts.length > 1 && (
          <path d={lineStr(fcLinePts)} fill="none" stroke="#b45309" strokeWidth="3" strokeDasharray="8 5" strokeLinejoin="round" strokeLinecap="round"/>
        )}

        {/* Points + value labels */}
        {points.map((p, i) => (
          <g key={`${p.kind}-${i}`}>
            <circle cx={x(i)} cy={y(p.units || 0)} r="4.5"
                    fill={p.kind === "fc" ? "#b45309" : "#f4c400"}
                    stroke="#fff" strokeWidth="2"/>
            <text x={x(i)} y={y(p.units || 0) - 10} fontSize="10" textAnchor="middle"
                  fontWeight="800" fill={p.kind === "fc" ? "#92400e" : "#475569"}>{p.units}</text>
            <text x={x(i)} y={H - padB + 16} fontSize="11" textAnchor="middle"
                  fontWeight="800" fill="#475569">{p.label}</text>
            <text x={x(i)} y={H - padB + 30} fontSize="10" textAnchor="middle"
                  fontWeight="700" fill="#94a3b8">{p.sub}</text>
          </g>
        ))}

        {/* Legend */}
        <g transform={`translate(${padL}, ${H - 8})`} fontSize="10" fontWeight="800">
          <line x1="0" y1="0" x2="18" y2="0" stroke="#f4c400" strokeWidth="3"/>
          <text x="22" y="3" fill="#475569">Histórico</text>
          <line x1="90" y1="0" x2="108" y2="0" stroke="#b45309" strokeWidth="3" strokeDasharray="6 3"/>
          <text x="112" y="3" fill="#475569">Forecast</text>
          <line x1="180" y1="0" x2="198" y2="0" stroke="#64748b" strokeWidth="2" strokeDasharray="5 3"/>
          <text x="202" y="3" fill="#475569">Tendencia</text>
        </g>
      </svg>
    </div>
  );
}

/**
 * (Reserved) — banner replaced by the consolidated view inside `SarimaPanel`.
 * The `StockDetailModal` component below is exported so the panel can reuse it.
 */

/**
 * (Reserved) — banner replaced by the consolidated view inside `SarimaPanel`.
 * The `StockAlertRow` component below is now rendered inline by that panel
 * when the operator expands the "Modelos con inventario crítico" accordion.
 */
function StockAlertRow({ alert, onClick }) {
  const isCrit = alert.severity === "critical";
  const accent = isCrit ? "#dc2626" : "#d97706";
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg p-3 bg-white border text-left hover:shadow-md transition cursor-pointer w-full"
      style={{ borderColor: isCrit ? "#fecaca" : "#fde68a", borderLeftColor: accent, borderLeftWidth: 3 }}
      data-testid={`stock-alert-${alert.brand}-${alert.model}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-display font-extrabold text-sm truncate" title={`${alert.brand} · ${alert.model}`}>
          {alert.brand} · {alert.model}
        </div>
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-extrabold flex-shrink-0"
              style={{ background: isCrit ? "#fee2e2" : "#fef3c7", color: accent }}>
          {isCrit ? "CRÍTICO" : "ALERTA"}
        </span>
      </div>
      <div className="text-xs font-bold text-gray-700 flex items-center gap-2 flex-wrap"
           style={{ fontVariantNumeric: "tabular-nums" }}>
        <span title="Ventas promedio mensuales (últimos 3m)">
          <span className="text-gray-400 font-semibold">Proyectado</span> {alert.expected_monthly}/mo
        </span>
        <span className="text-gray-300">·</span>
        <span title="Unidades con Estado 'Disponible' en inventario">
          <span className="text-gray-400 font-semibold">Stock</span> {alert.available_stock}
        </span>
        <span className="text-gray-300">·</span>
        <span style={{ color: accent }} title="Meses de cobertura al ritmo actual">
          {alert.coverage_months.toFixed(1)}m cobertura
        </span>
      </div>
    </button>
  );
}

/**
 * Full-screen modal with the drill-down for one (brand, model) stock alert.
 * Fetches last N sales + current stock and shows aging buckets so the manager
 * can decide whether to place a new import order.
 */
function StockDetailModal({ brand, model, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const showRefetchPill = useMinVisible(loading, 700);
  const [vinQuery, setVinQuery] = useState("");
  const [vinResult, setVinResult] = useState(null);
  const [vinSearching, setVinSearching] = useState(false);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ brand, model, limit: 20 }).toString();
    api.get(`/predictive/stock-detail?${qs}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [brand, model]);

  // Debounced global VIN search — hits /vin-lookup as user types.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = vinQuery.trim();
    if (q.length < 3) { setVinResult(null); setVinSearching(false); return; }
    setVinSearching(true);
    searchDebounceRef.current = setTimeout(() => {
      api.get(`/predictive/vin-lookup?q=${encodeURIComponent(q)}&limit=10`)
        .then((r) => setVinResult(r.data))
        .catch(() => setVinResult(null))
        .finally(() => setVinSearching(false));
    }, 350);
    return () => clearTimeout(searchDebounceRef.current);
  }, [vinQuery]);

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
  const fmtDays = (n) => n == null ? "—" : `${n}d`;

  const exportCsv = () => {
    if (!data) return;
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n;]/.test(s) ? `"${s}"` : s;
    };
    const lines = [];
    // Header block
    lines.push(`Detalle de stock;${brand};${model}`);
    lines.push(`Generado;${new Date().toISOString()}`);
    lines.push(`Ventas históricas;${data.summary.total_sold_ever}`);
    lines.push(`Disponibles ahora;${data.summary.total_available_now}`);
    lines.push(`Chasis más antiguo (días);${data.summary.oldest_avail_days ?? ""}`);
    lines.push(`Chasis más nuevo (días);${data.summary.newest_avail_days ?? ""}`);
    lines.push("");
    // Current stock
    lines.push(`Chasis disponibles (${data.current_stock.length})`);
    lines.push(["Chasis","Modelo Año","Color","Almacén","Ubicación","Estado","Días en inventario","Antigüedad"].map(esc).join(";"));
    data.current_stock.forEach((s) => {
      lines.push([s.chasis, s.model_year, s.color_ext, s.desc_almacen, s.ubicacion, s.estado, s.dias_en_inv, s.antiguedad].map(esc).join(";"));
    });
    lines.push("");
    // Recent sales
    lines.push(`Últimas ventas (${data.recent_sales.length})`);
    lines.push(["Chasis","Fecha","Días desde venta","Cliente","Showroom","Valor neto"].map(esc).join(";"));
    data.recent_sales.forEach((s) => {
      lines.push([s.chasis, s.date ? fmtDate(s.date) : "", s.days_ago, s.cliente, s.showroom, s.valor_neto].map(esc).join(";"));
    });
    const csv = "\uFEFF" + lines.join("\n");   // BOM so Excel opens UTF-8 correctly
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `stock_${brand}_${model}_${stamp}.csv`.replace(/\s+/g, "_");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyVins = async () => {
    if (!data || !data.current_stock.length) {
      toast.warning("No hay VINs disponibles para copiar");
      return;
    }
    const text = data.current_stock.map((s) => s.chasis).filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${data.current_stock.length} VIN${data.current_stock.length !== 1 ? "s" : ""} copiado${data.current_stock.length !== 1 ? "s" : ""}`,
        { description: "Pegar en WhatsApp o correo al importador" });
    } catch (e) {
      // Fallback: use a textarea + execCommand for older browsers / non-secure contexts.
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        toast.success(`${data.current_stock.length} VINs copiados`);
      } else {
        toast.error("No se pudo copiar automáticamente. Selecciona manualmente.");
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.6)" }}
      onClick={onClose}
      data-testid="stock-detail-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">Detalle de stock</div>
            <h2 className="font-display font-extrabold text-xl">{brand} · {model}</h2>
          </div>
          <div className="flex items-center gap-2">
            {data && data.current_stock.length > 0 && (
              <button
                type="button"
                onClick={copyVins}
                className="px-3 py-2 rounded-lg font-extrabold text-xs flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 transition"
                data-testid="stock-detail-copy-vins"
                title="Copiar la lista completa de VINs disponibles al portapapeles (uno por línea)"
              >
                <Copy size={13}/> Copiar VINs
              </button>
            )}
            {data && (
              <button
                type="button"
                onClick={exportCsv}
                className="px-3 py-2 rounded-lg font-extrabold text-xs flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 transition"
                data-testid="stock-detail-export-csv"
                title="Descargar chasis disponibles + últimas ventas como CSV (UTF-8, Excel-compatible)"
              >
                <Download size={13}/> Exportar CSV
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="stock-detail-close">
              <X size={20}/>
            </button>
          </div>
        </div>

        {loading && !data && (
          <div className="p-6 space-y-3">
            <SkeletonBlock height={72} testId="stock-detail-skeleton-stats"/>
            <SkeletonBlock height={220} testId="stock-detail-skeleton-table"/>
          </div>
        )}
        {showRefetchPill && data && <RefetchPill/>}

        {!loading && data && (
          <div className="overflow-y-auto flex-1">
            <div className="grid grid-cols-4 gap-3 px-6 py-4 border-b border-gray-100" style={{ background: "#f8fafc" }}>
              <StatMini label="Ventas históricas" value={data.summary.total_sold_ever} tone="#3b82f6"/>
              <StatMini label="Disponibles ahora" value={data.summary.total_available_now}
                        tone={data.summary.total_available_now === 0 ? "#dc2626" : "#16a34a"}/>
              <StatMini label="Chasis más antiguo"
                        value={data.summary.oldest_avail_days != null ? `${data.summary.oldest_avail_days}d` : "—"}
                        tone="#f59e0b" hint="Días en inventario"/>
              <StatMini label="Chasis más nuevo"
                        value={data.summary.newest_avail_days != null ? `${data.summary.newest_avail_days}d` : "—"}
                        tone="#64748b" hint="Días desde último ingreso"/>
            </div>

            {/* VIN search — locate any chassis across ALL brands/models */}
            <div className="px-6 py-3 border-b border-gray-100" data-testid="vin-search">
              <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 block mb-1">
                Buscar VIN (ubica cualquier chasis del inventario o ventas)
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input
                  type="text"
                  value={vinQuery}
                  onChange={(e) => setVinQuery(e.target.value.toUpperCase())}
                  placeholder="Últimos 3+ caracteres del chasis (ej: 136209, JYA…)"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold focus:outline-none focus:border-amber-400"
                  data-testid="vin-search-input"
                />
              </div>
              {vinQuery.length >= 3 && (
                <div className="mt-2" data-testid="vin-search-results">
                  {vinSearching ? (
                    <p className="text-xs font-semibold text-gray-400">Buscando…</p>
                  ) : vinResult && vinResult.total_matches === 0 ? (
                    <p className="text-xs font-semibold text-red-600">Sin coincidencias para &quot;{vinResult.query}&quot;.</p>
                  ) : vinResult ? (
                    <div className="grid md:grid-cols-2 gap-2">
                      {vinResult.inventory_matches.length > 0 && (
                        <div>
                          <div className="text-[10px] font-extrabold uppercase text-gray-500 mb-1">
                            En inventario ({vinResult.inventory_matches.length})
                          </div>
                          <div className="space-y-1">
                            {vinResult.inventory_matches.map((m) => (
                              <VinInventoryHit key={m.chasis} m={m}/>
                            ))}
                          </div>
                        </div>
                      )}
                      {vinResult.sales_matches.length > 0 && (
                        <div>
                          <div className="text-[10px] font-extrabold uppercase text-gray-500 mb-1">
                            En ventas ({vinResult.sales_matches.length})
                          </div>
                          <div className="space-y-1">
                            {vinResult.sales_matches.map((m) => (
                              <VinSaleHit key={m.chasis} m={m}/>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 divide-x divide-gray-100">
              <div className="p-6">
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <TrendingUp size={14}/> Últimas ventas ({data.recent_sales.length})
                </h3>
                {data.recent_sales.length === 0 ? (
                  <p className="text-xs font-semibold text-gray-400">Sin ventas registradas.</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.recent_sales.map((s, i) => (
                      <div key={s.chasis || i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs"
                           style={{ background: "#fafafa" }}>
                        <span className="font-mono font-bold text-[10px] text-gray-500 truncate flex-1" title={s.chasis}>
                          {(s.chasis || "").slice(-8)}
                        </span>
                        <span className="font-bold text-gray-700 truncate" style={{ maxWidth: 140 }} title={s.cliente}>
                          {s.cliente || "—"}
                        </span>
                        <span className="text-gray-500 font-semibold flex-shrink-0">{fmtDate(s.date)}</span>
                        <span className="font-extrabold flex-shrink-0"
                              style={{ color: s.days_ago != null && s.days_ago < 30 ? "#16a34a" : "#64748b" }}>
                          {fmtDays(s.days_ago)} ago
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-6">
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <PackageX size={14}/> Chasis disponibles ({data.current_stock.length})
                </h3>
                {data.current_stock.length === 0 ? (
                  <div className="p-3 rounded-lg text-xs font-bold" style={{ background: "#fee2e2", color: "#991b1b" }}>
                    <PackageX size={12} className="inline mr-1"/> Sin unidades disponibles. <strong>Considera solicitar reposición al importador.</strong>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {data.current_stock.map((s, i) => {
                      const days = s.dias_en_inv || 0;
                      const stale = days > 180;
                      return (
                        <div key={s.chasis || i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs"
                             style={{ background: stale ? "#fef3c7" : "#fafafa" }}>
                          <span className="font-mono font-bold text-[10px] text-gray-500 truncate flex-1" title={s.chasis}>
                            {(s.chasis || "").slice(-8)}
                          </span>
                          <span className="text-gray-600 font-semibold" style={{ fontSize: 11 }}>
                            {s.model_year || ""} {s.color_ext || ""}
                          </span>
                          <span className="text-gray-500 font-bold text-[10px] truncate" style={{ maxWidth: 100 }} title={s.desc_almacen}>
                            {s.desc_almacen || "—"}
                          </span>
                          <span className="font-extrabold flex-shrink-0"
                                style={{ color: stale ? "#b45309" : days > 60 ? "#d97706" : "#16a34a" }}>
                            {days}d
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!loading && !data && (
          <div className="p-8 text-center text-sm font-semibold text-red-600">
            No se pudo cargar el detalle.
          </div>
        )}
      </div>
    </div>
  );
}

function StatMini({ label, value, tone, hint }) {
  return (
    <div>
      <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">{label}</div>
      <div className="font-display font-extrabold text-2xl" style={{ color: tone }}>{value}</div>
      {hint && <div className="text-[10px] font-semibold text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function VinInventoryHit({ m }) {
  const days = m.dias_en_inv || 0;
  const bg = m.available ? "#f0fdf4" : "#fef3c7";
  return (
    <div className="p-2 rounded-lg border text-xs" style={{ background: bg, borderColor: m.available ? "#bbf7d0" : "#fde68a" }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono font-extrabold text-[11px] truncate">{m.chasis}</span>
        <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded"
              style={{ background: m.available ? "#dcfce7" : "#fef3c7",
                       color: m.available ? "#166534" : "#92400e" }}>
          {m.available ? "DISPONIBLE" : (m.estado || "OCUPADO").toUpperCase().slice(0, 20)}
        </span>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 flex-wrap">
        <span>{m.brand} · {m.model} {m.model_year || ""} {m.color_ext || ""}</span>
      </div>
      <div className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 mt-1">
        <MapPin size={10}/> {m.desc_almacen || m.ubicacion || "—"}
        <span className="ml-auto font-extrabold" style={{ color: days > 180 ? "#b45309" : days > 60 ? "#d97706" : "#16a34a" }}>
          {days}d
        </span>
      </div>
    </div>
  );
}

function VinSaleHit({ m }) {
  return (
    <div className="p-2 rounded-lg border text-xs" style={{ background: "#fafafa", borderColor: "#e5e7eb" }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono font-extrabold text-[11px] truncate">{m.chasis}</span>
        <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded"
              style={{ background: "#dbeafe", color: "#1e40af" }}>VENDIDO</span>
      </div>
      <div className="text-[11px] font-semibold text-gray-700">
        {m.brand} · {m.model}
      </div>
      <div className="flex items-center gap-2 text-[11px] font-semibold text-gray-500 mt-1">
        <span>{m.cliente || "—"}</span>
        <span className="ml-auto font-extrabold text-gray-500">
          {m.days_ago != null ? `${m.days_ago}d ago` : "—"}
        </span>
      </div>
    </div>
  );
}

export default function PredictivePage() {
  const pageRef = useRef(null);
  const { filters, setFilters, setF, reset } = useNormalizedFilter(
    "forecast_filters_v2", DEFAULT_FILTERS,
  );
  const [options, setOptions] = useState({ years: [], brands: [], branches: [], models: [], months: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const showRefetchPill = useMinVisible(loading, 700);
  const [stockDetail, setStockDetail] = useState(null);   // { brand, model } | null

  useEffect(() => {
    api.get("/predictive/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  // Auto-heal stale filter values. If localStorage was seeded with values that
  // no longer exist in the current options (e.g. year 2027 from an earlier
  // dataset), silently drop them so the user isn't stuck with a query that
  // returns 0 rows. Runs once options finish loading.
  const optionsLoaded = (options.years?.length || 0) > 0;
  useEffect(() => {
    if (!optionsLoaded) return;
    const validSets = {
      year:   new Set((options.years || []).map(String)),
      month:  new Set((options.months || []).map(String)),
      brand:  new Set((options.brands || []).map(String)),
      model:  new Set((options.models || []).map(String)),
      branch: new Set((options.branches || []).map(String)),
    };
    setFilters((cur) => {
      let dirty = false;
      const next = { ...cur };
      for (const [k, valid] of Object.entries(validSets)) {
        const arr = Array.isArray(cur[k]) ? cur[k] : [];
        const cleaned = arr.filter((v) => valid.has(String(v)));
        if (cleaned.length !== arr.length) {
          next[k] = cleaned;
          dirty = true;
        }
      }
      return dirty ? next : cur;
    });
  }, [optionsLoaded]);

  useEffect(() => {
    setLoading(true);
    // Serialize array filters as comma-joined strings (backend uses _coerce_list).
    const qs = Object.entries(filters)
      .map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : v])
      .filter(([, v]) => v)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    api.get(`/predictive/summary?${qs}`)
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [filters]);

  const rateColor = (r) => r >= 12 ? "#16a34a" : r >= 6 ? "#f97316" : "#dc2626";

  // Brand color map for trend rows (top global brands)
  const brandColorMap = useMemo(() => {
    if (!summary?.brands_performance) return {};
    const map = {};
    summary.brands_performance.slice(0, 10).forEach((b, i) => { map[b.brand] = brandColor(i); });
    return map;
  }, [summary]);

  // Historical trend rows for the horizontal table
  const historyRows = useMemo(() => summary?.trend || [], [summary]);
  const maxUnits = useMemo(() => Math.max(1, ...historyRows.map((r) => r.sales || 0)), [historyRows]);

  // Forecast vs history series for the combined timeseries chart
  const tsChart = useMemo(() => {
    const hist = (summary?.trend || []).slice(-9).map((t) => ({
      label: t.month, sub: String(t.year).slice(2), units: t.sales, revenue: t.revenue, kind: "hist",
    }));
    const fc = (summary?.forecast || []).map((f) => ({
      label: f.month, sub: String(f.year).slice(2), units: f.expected_sales, revenue: f.expected_revenue, kind: "fc",
    }));
    const all = [...hist, ...fc];
    const max = Math.max(1, ...all.map((p) => p.units || 0));
    return { points: all, max };
  }, [summary]);

  return (
    <div ref={pageRef} className="px-6 py-6 max-w-[1600px] mx-auto" data-testid="predictive-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3" data-pdf-hide>
        <div>
          <div className="flex items-center gap-3">
            <TrendingUp size={28} strokeWidth={2.4} style={{ color: "#f4c400" }}/>
            <h1 className="font-display font-extrabold" style={{ fontSize: 28, letterSpacing: "-0.02em" }}>
              Forecast e Inventario
            </h1>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Serie histórica · Forecast 3 meses · Calculadora de meta · Alertas de stock · Inventario
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PdfExportButton
            targetRef={pageRef}
            filename="forecast-inventario"
            title="SDM · Forecast e Inventario"
            subtitle={`${filters.year?.join(",") || ""} ${filters.brand?.join(",") || ""}`.trim()}
            testid="predictive-pdf-btn"
          />
          <button
            type="button"
            onClick={reset}
            className="btn flex items-center gap-2"
            data-testid="predictive-reset"
            style={{ padding: "8px 14px" }}
          >
            <RefreshCw size={14}/> Borrar Filtros
          </button>
        </div>
      </div>

      {/* Filters — collapsible on mobile via <details>. Always open ≥ 768px. */}
      <details className="lp2-filters-details surface p-4 mb-5" data-testid="predictive-filters"
               open={typeof window !== "undefined" && window.innerWidth >= 768}>
        <summary className="lp2-filters-summary">
          <span className="lp2-filters-summary-label">Filtros</span>
          <ChevronDown size={16} className="lp2-filters-summary-chev"/>
        </summary>
        <div className="predictive-filter-grid">
          {[
            ["Año",              "year",   (options.years || []).map((y) => ({ value: String(y), label: String(y) }))],
            ["Mes",              "month",  (options.months || []).map((m) => ({ value: m, label: m }))],
            ["Marca",            "brand",  (options.brands || []).map((b) => ({ value: b, label: b }))],
            ["Modelo",           "model",  (options.models || []).map((m) => ({ value: m, label: m }))],
            ["Unidad de Negocio","branch", (options.branches || []).map((b) => ({ value: b, label: b }))],
          ].map(([label, key, opts]) => (
            <div key={key}>
              <label className="label-up block mb-2">{label}</label>
              <MultiSelect
                value={filters[key]}
                onChange={(v) => setF(key, v)}
                placeholder={label}
                options={opts}
                testid={`predictive-filter-${key}`}
              />
            </div>
          ))}
        </div>
      </details>

      {loading && !summary && (
        <div className="space-y-4" data-testid="predictive-skeleton">
          <SkeletonRibbon count={4}/>
          <SkeletonBlock height={320}/>
          <SkeletonBlock height={240}/>
        </div>
      )}
      {showRefetchPill && summary && <RefetchPill/>}

      {summary && !loading && (
        <>
          {/* KPIs — 2 cols mobile / 4 cols desktop */}
          <section className="lp2-kpi-grid mb-5">
            <KpiCard label="Leads (período)" value={fmt(summary.leads_total)}
              sub="Total de leads que cumplen filtros" color="#3b82f6"/>
            <KpiCard label="Ventas (período)" value={fmt(summary.sales_total)}
              sub="Unidades facturadas que cumplen filtros" color="#16a34a"/>
            <KpiCard label="Conversión macro" value={fmtPct(summary.conversion_rate_macro)}
              sub="Ratio sales ÷ leads del período. Benchmark: 12-15%"
              color={rateColor(summary.conversion_rate_macro)}/>
            <KpiCard label="Forecast prom. 3m"
              value={fmt((summary.forecast || []).reduce((a, f) => a + (f.expected_sales || 0), 0) / Math.max(1, (summary.forecast || []).length))}
              sub="Promedio de unidades proyectadas próximos 3 meses" color="#f4c400"/>
          </section>

          {/* Forecast timeseries — anexo (moved above Conversión por Marca) */}
          <section className="surface p-4 md:p-6 mb-5" data-testid="predictive-forecast">
            <div className="flex items-center gap-2 mb-4">
              <LineChart size={18} style={{ color: "#f4c400" }}/>
              <h2 className="text-base md:text-lg font-extrabold font-display">Forecast — Histórico + Proyección 3 meses</h2>
            </div>

            {/* Forecast cards on top — 1 col mobile, 3 cols ≥ 640px */}
            <div className="predictive-forecast-cards mb-5">
              {(summary.forecast || []).map((f) => (
                <div key={f.horizon} className="surface p-4" style={{ borderLeft: "3px solid #f4c400" }} data-testid={`forecast-card-${f.horizon}`}>
                  <div className="text-xs font-extrabold uppercase tracking-wider" style={{ color: "#92400e" }}>
                    +{f.horizon}m · {f.label}
                  </div>
                  <div className="font-display font-extrabold mt-2" style={{ fontSize: 22 }}>{fmt(f.expected_sales)} uds</div>
                  <div className="text-xs text-gray-500 font-semibold mt-1">{fmtMoneyShort(f.expected_revenue)}</div>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 font-semibold mb-3">
              Línea sólida amarilla = unidades reales · línea punteada = proyección · línea gris = tendencia lineal sobre últimos 6 meses.
            </p>

            {/* SVG line chart with trend line */}
            <ForecastLineChart points={tsChart.points} />
          </section>

          {/* SARIMA Executive Panel — Fase 2. Stock alerts consolidados
              adentro (badge + acordeón con drill-down a VINs). */}
          <SarimaPanel
            stockAlerts={summary.stock_alerts || []}
            renderAlertRow={(a) => (
              <StockAlertRow
                key={`${a.brand}-${a.model}`}
                alert={a}
                onClick={() => setStockDetail({ brand: a.brand, model: a.model })}
              />
            )}
          />

          {/* Conversion by Brand */}
          <section className="surface p-4 md:p-6 mb-5" data-testid="predictive-brands">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={18}/>
              <h2 className="text-base md:text-lg font-extrabold font-display">Conversión por Marca</h2>
            </div>
            <div className="predictive-scrollable-table">
              <table className="w-full text-sm predictive-mobile-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    {["Marca", "Leads", "Ventas", "Conv.", "Revenue (DOP)", "Margen (DOP)"].map((h, i) => (
                      <th key={h} className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 py-2"
                          style={{ textAlign: i === 0 ? "left" : "right" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summary.brands_performance.slice(0, 12).map((b) => (
                    <tr key={b.brand} style={{ borderBottom: "1px solid #f1f5f9" }}
                        data-testid={`predictive-brand-${b.brand}`}>
                      <td className="py-3 font-display font-extrabold" style={{ color: "#111827" }}>{b.brand}</td>
                      <td className="py-3 text-right font-bold" style={{ color: "#64748b", fontVariantNumeric: "tabular-nums" }}>{fmt(b.leads)}</td>
                      <td className="py-3 text-right font-display font-extrabold" style={{ color: "#111827", fontVariantNumeric: "tabular-nums" }}>{fmt(b.sales)}</td>
                      <td className="py-3 text-right">
                        <span className="pill font-extrabold" style={{
                          background: b.rate >= 12 ? "#dcfce7" : b.rate >= 6 ? "#fed7aa" : "#fee2e2",
                          color: rateColor(b.rate), padding: "3px 10px", fontSize: 12,
                        }}>{fmtPct(b.rate)}</span>
                      </td>
                      <td className="py-3 text-right font-bold" style={{ color: "#475569", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(b.revenue)}</td>
                      <td className="py-3 text-right font-bold" style={{ color: "#16a34a", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(b.margin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Historical timeseries — horizontal layout */}
          <section className="surface p-4 md:p-6 mb-5" data-testid="predictive-history">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <BarChart3 size={18}/>
                <h2 className="text-base md:text-lg font-extrabold font-display">Serie Histórica de Ventas por Mes</h2>
              </div>
              <span className="text-xs font-bold text-gray-500">
                Últimos {historyRows.length} meses · unidades y monto DOP, con desglose por marca
              </span>
            </div>
            <div className="predictive-scrollable-table">
              <table className="w-full text-sm predictive-mobile-table" data-testid="history-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <th className="text-left text-[10px] font-extrabold uppercase tracking-wider text-gray-500 py-2" style={{ width: 110 }}>Mes</th>
                    <th className="text-right text-[10px] font-extrabold uppercase tracking-wider text-gray-500 py-2" style={{ width: 90 }}>Unidades</th>
                    <th className="text-right text-[10px] font-extrabold uppercase tracking-wider text-gray-500 py-2" style={{ width: 130 }}>Monto (DOP)</th>
                    <th className="text-left text-[10px] font-extrabold uppercase tracking-wider text-gray-500 py-2">Distribución por marca</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row) => {
                    const total = row.by_brand.reduce((a, b) => a + b.sales, 0) || row.sales || 1;
                    return (
                      <tr key={`${row.year}-${row.month}`} style={{ borderBottom: "1px solid #f1f5f9" }}
                          data-testid={`history-row-${row.year}-${row.month}`}>
                        <td className="py-3">
                          <div className="font-display font-extrabold text-sm" style={{ color: "#111827" }}>{row.month}</div>
                          <div className="text-xs text-gray-500 font-semibold">{row.year}</div>
                        </td>
                        <td className="py-3 text-right font-display font-extrabold" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {fmt(row.sales)}
                          <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" style={{ width: 70, marginLeft: "auto" }}>
                            <div style={{ width: `${(row.sales / maxUnits) * 100}%`, height: "100%", background: "linear-gradient(90deg, #f4c400, #f59e0b)" }}/>
                          </div>
                        </td>
                        <td className="py-3 text-right font-bold" style={{ color: "#475569", fontVariantNumeric: "tabular-nums" }}>{fmtMoneyShort(row.revenue)}</td>
                        <td className="py-3">
                          <div className="flex w-full h-5 rounded overflow-hidden" style={{ background: "#f1f5f9" }}>
                            {row.by_brand.map((b, i) => {
                              const w = (b.sales / total) * 100;
                              const c = brandColorMap[String(b.brand).toUpperCase()] || brandColor(i);
                              return (
                                <div key={b.brand} title={`${b.brand}: ${fmt(b.sales)} uds · ${fmtMoneyShort(b.revenue)}`}
                                     style={{ width: `${w}%`, background: c }}/>
                              );
                            })}
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                            {row.by_brand.slice(0, 4).map((b, i) => {
                              const c = brandColorMap[String(b.brand).toUpperCase()] || brandColor(i);
                              return (
                                <span key={b.brand} className="text-[11px] font-bold flex items-center gap-1" style={{ color: "#475569" }}>
                                  <span style={{ width: 8, height: 8, borderRadius: 2, background: c, display: "inline-block" }}/>
                                  {b.brand} <span className="text-gray-400 font-semibold">{fmt(b.sales)}u</span>
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {historyRows.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-gray-400 font-semibold">Sin datos para los filtros aplicados</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Full-inventory explorer — filter, paginate, export, copy VINs */}
          <InventorySearchSection/>
        </>
      )}

      {/* Shared VIN-detail modal, opened from either the SarimaPanel alerts
          accordion or from any other stock drill-down entry point. */}
      {stockDetail && (
        <StockDetailModal
          brand={stockDetail.brand}
          model={stockDetail.model}
          onClose={() => setStockDetail(null)}
        />
      )}
    </div>
  );
}
